<template>
  <div class="hms-page">
    <Navigation />

    <div class="container hms-page-body">
      <div class="d-flex align-items-center justify-content-between mb-3 hms-page-header">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="hms-back" title="Back">
            <i class="mdi mdi-arrow-left"></i>
          </a>
          <h4 class="hms-page-title m-0"><i class="mdi mdi-share-variant me-2"></i>{{ $t('page.referrals_out', 'e-Referrals') }}</h4>
        </div>
        <div class="d-flex gap-2">
          <button class="hms-btn hms-btn-success" @click="openCreate('outbound')"><i class="mdi mdi-arrow-right"></i> New referral OUT</button>
          <button class="hms-btn hms-btn-secondary" @click="openCreate('inbound')"><i class="mdi mdi-arrow-left"></i> Log inbound</button>
          <button class="hms-btn hms-btn-blue" @click="load"><i class="mdi mdi-refresh"></i></button>
        </div>
      </div>

      <!-- KPI -->
      <div class="hms-stat-row">
        <div class="hms-stat"><div class="hms-stat-num">{{ stats.out_drafts || 0 }}</div><div class="hms-stat-lbl">Drafts</div></div>
        <div class="hms-stat"><div class="hms-stat-num hms-stat-warning">{{ stats.out_pending || 0 }}</div><div class="hms-stat-lbl">Out — pending</div></div>
        <div class="hms-stat"><div class="hms-stat-num hms-stat-success">{{ stats.out_accepted || 0 }}</div><div class="hms-stat-lbl">Out — accepted</div></div>
        <div class="hms-stat"><div class="hms-stat-num hms-stat-info">{{ stats.in_received || 0 }}</div><div class="hms-stat-lbl">In — to triage</div></div>
        <div class="hms-stat"><div class="hms-stat-num hms-stat-info">{{ stats.in_transit || 0 }}</div><div class="hms-stat-lbl">In transit</div></div>
        <div class="hms-stat"><div class="hms-stat-num hms-stat-success">{{ stats.arrived_today || 0 }}</div><div class="hms-stat-lbl">Arrived today</div></div>
        <div class="hms-stat"><div class="hms-stat-num hms-stat-danger">{{ stats.emergencies || 0 }}</div><div class="hms-stat-lbl">Emergencies</div></div>
      </div>

      <!-- TABS -->
      <div class="hms-tab-row">
        <button class="hms-tab" :class="{ active: direction==='out' }" @click="direction='out'; load()"><i class="mdi mdi-arrow-right me-1"></i>Outbound</button>
        <button class="hms-tab" :class="{ active: direction==='in' }" @click="direction='in'; load()"><i class="mdi mdi-arrow-left me-1"></i>Inbound</button>
        <button class="hms-tab" :class="{ active: direction==='counter' }" @click="direction='counter'; load()"><i class="mdi mdi-swap-horizontal me-1"></i>Counter-referrals</button>
      </div>

      <table class="hms-table">
        <thead>
          <tr>
            <th>#</th><th>Patient</th><th>Reason / Specialty</th>
            <th>{{ direction === 'in' ? 'From' : 'To' }}</th>
            <th>Urgency</th><th>Transport</th><th>Status</th><th>Created</th><th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="r in referrals" :key="r.referral_id" :class="rowClass(r)">
            <td><strong>{{ r.document_no }}</strong></td>
            <td>{{ r.patient_name }}<div class="hms-muted-sm">{{ r.patient_number }} · {{ r.sex }} · {{ ageYears(r.date_of_birth) }}y</div></td>
            <td>
              <div>{{ r.referral_reason }}</div>
              <div class="hms-muted-sm">{{ r.receiving_specialty || r.receiving_department || '—' }}</div>
            </td>
            <td>
              <strong>{{ direction === 'in' ? (r.referring_facility_name || '—') : (r.receiving_facility_name || '—') }}</strong>
              <div v-if="(direction === 'in' ? r.referring_facility_code : r.receiving_facility_code)" class="hms-muted-sm">
                KMHFL: {{ direction === 'in' ? r.referring_facility_code : r.receiving_facility_code }}
              </div>
            </td>
            <td><span class="hms-pill" :class="urgencyPill(r.urgency)">{{ r.urgency }}</span></td>
            <td>
              <span v-if="r.transport_needed" class="hms-pill hms-pill-warn"><i class="mdi mdi-ambulance"></i> {{ formatTransport(r.transport_mode) }}</span>
              <span v-else class="hms-muted-sm">{{ formatTransport(r.transport_mode) }}</span>
            </td>
            <td><span class="hms-pill" :class="statusPill(r.status)">{{ formatStatus(r.status) }}</span></td>
            <td class="hms-muted-sm">{{ formatDt(r.created_at) }}</td>
            <td><button class="hms-btn hms-btn-blue hms-btn-sm" @click="openDetail(r)"><i class="mdi mdi-eye"></i> Open</button></td>
          </tr>
          <tr v-if="!referrals.length"><td colspan="9" class="text-center hms-pad hms-muted">No referrals.</td></tr>
        </tbody>
      </table>
    </div>

    <!-- ============ CREATE MODAL ============ -->
    <div v-if="createModal" class="hms-modal-bg" @click.self="createModal=false">
      <div class="hms-modal" style="width:880px">
        <div class="hms-modal-head">
          <h5>{{ form.referral_type === 'inbound' ? 'Log inbound referral' : 'New outbound referral' }}</h5>
          <button class="hms-icon-btn" @click="createModal=false"><i class="mdi mdi-close"></i></button>
        </div>
        <div class="hms-modal-body">
          <div class="row g-2">
            <div class="col-3"><label class="hms-label">Patient ID *</label><input v-model.number="form.patient_id" type="number" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Urgency</label>
              <select v-model="form.urgency" class="hms-input">
                <option value="routine">Routine</option><option value="urgent">Urgent</option><option value="emergency">Emergency</option>
              </select>
            </div>
            <div class="col-3"><label class="hms-label">Transport mode</label>
              <select v-model="form.transport_mode" class="hms-input">
                <option value="none">None</option><option value="self">Self</option>
                <option value="ambulance_bls">Ambulance (BLS)</option><option value="ambulance_als">Ambulance (ALS)</option>
                <option value="air">Air</option><option value="police">Police</option>
              </select>
            </div>
            <div class="col-3 d-flex align-items-end"><label><input type="checkbox" v-model="form.transport_needed" :true-value="1" :false-value="0" /> Transport required</label></div>

            <template v-if="form.referral_type === 'outbound'">
              <div class="col-12 hms-section-lbl mt-2">Receiving facility</div>
              <div class="col-6"><label class="hms-label">Facility name *</label><input v-model="form.receiving_facility_name" class="hms-input" placeholder="e.g. Narok County Referral Hospital" /></div>
              <div class="col-3"><label class="hms-label">KMHFL code</label><input v-model="form.receiving_facility_code" class="hms-input" /></div>
              <div class="col-3"><label class="hms-label">Contact</label><input v-model="form.receiving_contact" class="hms-input" /></div>
              <div class="col-3"><label class="hms-label">Department</label><input v-model="form.receiving_department" class="hms-input" /></div>
              <div class="col-3"><label class="hms-label">Specialty</label>
                <select v-model="form.receiving_specialty" class="hms-input">
                  <option value="">—</option>
                  <option>Internal Medicine</option><option>Paediatrics</option><option>OB/GYN</option>
                  <option>Surgery</option><option>Orthopaedics</option><option>Psychiatry</option>
                  <option>Dermatology</option><option>ENT</option><option>Ophthalmology</option>
                  <option>Cardiology</option><option>Oncology</option><option>Nephrology</option>
                  <option>Endocrinology</option><option>NICU</option><option>ICU</option>
                </select>
              </div>
            </template>

            <template v-if="form.referral_type === 'inbound'">
              <div class="col-12 hms-section-lbl mt-2">Referring facility</div>
              <div class="col-6"><label class="hms-label">Facility name *</label><input v-model="form.referring_facility_name" class="hms-input" /></div>
              <div class="col-3"><label class="hms-label">KMHFL code</label><input v-model="form.referring_facility_code" class="hms-input" /></div>
              <div class="col-3"><label class="hms-label">Contact</label><input v-model="form.referring_contact" class="hms-input" /></div>
            </template>

            <div class="col-12 hms-section-lbl mt-2">Clinical</div>
            <div class="col-12"><label class="hms-label">Reason for referral *</label><textarea v-model="form.referral_reason" class="hms-input" rows="2" placeholder="Why is this patient being referred?"></textarea></div>
            <div class="col-12"><label class="hms-label">Presenting complaint</label><textarea v-model="form.presenting_complaint" class="hms-input" rows="2"></textarea></div>
            <div class="col-12"><label class="hms-label">History</label><textarea v-model="form.history" class="hms-input" rows="2"></textarea></div>
            <div class="col-12"><label class="hms-label">Examination findings</label><textarea v-model="form.examination_findings" class="hms-input" rows="2"></textarea></div>
            <div class="col-6"><label class="hms-label">Provisional diagnosis</label><input v-model="form.provisional_diagnosis" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">ICD-11 codes</label><input v-model="form.icd11_codes" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">Investigations done</label><textarea v-model="form.investigations_done" class="hms-input" rows="2"></textarea></div>
            <div class="col-6"><label class="hms-label">Investigation results</label><textarea v-model="form.investigations_results" class="hms-input" rows="2"></textarea></div>
            <div class="col-6"><label class="hms-label">Current medications</label><textarea v-model="form.current_medications" class="hms-input" rows="2"></textarea></div>
            <div class="col-6"><label class="hms-label">Treatment given</label><textarea v-model="form.treatment_given" class="hms-input" rows="2"></textarea></div>
            <div class="col-12"><label class="hms-label">Known allergies</label><input v-model="form.known_allergies" class="hms-input" /></div>

            <div class="col-12 hms-section-lbl mt-2">Vitals at referral</div>
            <div class="col-2"><label class="hms-label">Temp (°C)</label><input v-model.number="form.vital_temp" type="number" step="0.1" class="hms-input" /></div>
            <div class="col-2"><label class="hms-label">Pulse</label><input v-model.number="form.vital_pulse" type="number" class="hms-input" /></div>
            <div class="col-2"><label class="hms-label">Resp</label><input v-model.number="form.vital_resp" type="number" class="hms-input" /></div>
            <div class="col-2"><label class="hms-label">BP</label><input v-model="form.vital_bp" placeholder="120/80" class="hms-input" /></div>
            <div class="col-2"><label class="hms-label">SpO2 %</label><input v-model.number="form.vital_spo2" type="number" class="hms-input" /></div>
            <div class="col-2"><label class="hms-label">GCS</label><input v-model.number="form.vital_gcs" type="number" class="hms-input" /></div>

            <div class="col-12 hms-section-lbl mt-2">Logistics</div>
            <div class="col-6"><label class="hms-label">Accompanying staff</label><input v-model="form.accompanying_staff" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Expected arrival</label><input v-model="form.expected_arrival" type="datetime-local" class="hms-input" /></div>
            <div class="col-3 d-flex align-items-end"><label><input type="checkbox" v-model="form.consent_obtained" :true-value="1" :false-value="0" /> Consent obtained</label></div>
            <div v-if="form.consent_obtained" class="col-12"><label class="hms-label">Consent signed by</label><input v-model="form.consent_signed_by" class="hms-input" placeholder="Patient / Guardian name" /></div>
          </div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="createModal=false">Cancel</button>
          <button class="hms-btn hms-btn-secondary" :disabled="saving" @click="saveDraft">Save draft</button>
          <button class="hms-btn hms-btn-success" :disabled="saving" @click="saveAndSend">{{ saving?'Sending…':'Send referral' }}</button>
        </div>
      </div>
    </div>

    <!-- ============ DETAIL MODAL ============ -->
    <div v-if="detailModal" class="hms-modal-bg" @click.self="detailModal=false">
      <div class="hms-modal hms-detail-modal">
        <div class="hms-modal-head">
          <div>
            <h5 class="m-0">{{ active.document_no }}
              <span class="hms-pill ms-2" :class="statusPill(active.status)">{{ formatStatus(active.status) }}</span>
              <span class="hms-pill ms-1" :class="urgencyPill(active.urgency)">{{ active.urgency }}</span>
            </h5>
            <div class="hms-muted-sm">
              {{ active.patient_name }} · {{ active.patient_number }} · {{ active.sex }} · {{ ageYears(active.date_of_birth) }}y
            </div>
          </div>
          <button class="hms-icon-btn" @click="detailModal=false"><i class="mdi mdi-close"></i></button>
        </div>
        <div class="hms-modal-body">
          <div class="row g-3">
            <div class="col-6">
              <div class="hms-card">
                <div class="hms-card-head">Referral details</div>
                <div class="hms-card-body">
                  <div><strong>From:</strong> {{ active.referring_facility_name || '—' }}<span v-if="active.referring_facility_code"> ({{ active.referring_facility_code }})</span></div>
                  <div><strong>To:</strong> {{ active.receiving_facility_name || '—' }}<span v-if="active.receiving_facility_code"> ({{ active.receiving_facility_code }})</span></div>
                  <div><strong>Department:</strong> {{ active.receiving_department || '—' }} · <strong>Specialty:</strong> {{ active.receiving_specialty || '—' }}</div>
                  <div><strong>Reason:</strong> {{ active.referral_reason }}</div>
                  <div v-if="active.provisional_diagnosis"><strong>Dx:</strong> {{ active.provisional_diagnosis }} <span v-if="active.icd11_codes" class="hms-muted-sm">({{ active.icd11_codes }})</span></div>
                  <hr />
                  <div v-if="active.presenting_complaint"><strong>CC:</strong> {{ active.presenting_complaint }}</div>
                  <div v-if="active.history"><strong>Hx:</strong> {{ active.history }}</div>
                  <div v-if="active.examination_findings"><strong>O/E:</strong> {{ active.examination_findings }}</div>
                  <div v-if="active.investigations_done"><strong>Ix done:</strong> {{ active.investigations_done }}</div>
                  <div v-if="active.investigations_results"><strong>Ix results:</strong> {{ active.investigations_results }}</div>
                  <div v-if="active.treatment_given"><strong>Treatment:</strong> {{ active.treatment_given }}</div>
                  <div v-if="active.current_medications"><strong>Current meds:</strong> {{ active.current_medications }}</div>
                  <div v-if="active.known_allergies"><strong class="st-bad">Allergies:</strong> {{ active.known_allergies }}</div>
                  <div v-if="hasVitals" class="hms-muted-sm mt-2">
                    Vitals: T {{ active.vital_temp || '—' }}°C · HR {{ active.vital_pulse || '—' }} · RR {{ active.vital_resp || '—' }} · BP {{ active.vital_bp || '—' }} · SpO2 {{ active.vital_spo2 || '—' }}% · GCS {{ active.vital_gcs || '—' }}
                  </div>
                </div>
              </div>

              <div class="hms-card mt-2">
                <div class="hms-card-head">Logistics</div>
                <div class="hms-card-body">
                  <div><strong>Transport:</strong> {{ formatTransport(active.transport_mode) }} <span v-if="active.transport_needed" class="hms-pill hms-pill-warn">required</span></div>
                  <div v-if="active.accompanying_staff"><strong>Accompanying:</strong> {{ active.accompanying_staff }}</div>
                  <div v-if="active.expected_arrival"><strong>ETA:</strong> {{ formatDt(active.expected_arrival) }}</div>
                  <div v-if="active.consent_obtained"><strong>Consent:</strong> ✓ {{ active.consent_signed_by }}</div>
                  <div v-if="active.bed_offered_id"><strong>Bed:</strong> {{ active.offered_bed_number }} ({{ active.offered_ward_name }})</div>
                </div>
              </div>

              <div v-if="active.outcome_summary || active.feedback_to_referrer" class="hms-card mt-2">
                <div class="hms-card-head">Outcome / counter-referral</div>
                <div class="hms-card-body">
                  <div v-if="active.outcome_summary"><strong>Outcome:</strong> {{ active.outcome_summary }}</div>
                  <div v-if="active.discharge_diagnosis"><strong>Discharge Dx:</strong> {{ active.discharge_diagnosis }}</div>
                  <div v-if="active.discharge_medications"><strong>Discharge Rx:</strong> {{ active.discharge_medications }}</div>
                  <div v-if="active.follow_up_plan"><strong>Follow-up:</strong> {{ active.follow_up_plan }}</div>
                  <div v-if="active.feedback_to_referrer" class="mt-2 hms-feedback"><strong>Feedback to referrer:</strong> {{ active.feedback_to_referrer }}</div>
                </div>
              </div>
            </div>

            <div class="col-6">
              <!-- Action buttons -->
              <div class="hms-card">
                <div class="hms-card-head">Actions</div>
                <div class="hms-card-body d-flex gap-2 flex-wrap">
                  <button v-if="active.status==='draft'" class="hms-btn hms-btn-success hms-btn-sm" @click="act('send')"><i class="mdi mdi-send"></i> Send</button>
                  <template v-if="active.direction==='in'">
                    <button v-if="active.status==='sent' || active.status==='received'" class="hms-btn hms-btn-blue hms-btn-sm" @click="openBedFinder"><i class="mdi mdi-bed"></i> Find bed & accept</button>
                    <button v-if="['sent','received'].includes(active.status)" class="hms-btn hms-btn-secondary hms-btn-sm" @click="act('reject', { rejection_reason: prompt('Rejection reason?') })"><i class="mdi mdi-close-circle"></i> Reject</button>
                    <button v-if="active.status==='accepted'" class="hms-btn hms-btn-warning hms-btn-sm" @click="act('depart')"><i class="mdi mdi-ambulance"></i> Mark departed</button>
                    <button v-if="['accepted','in_transit'].includes(active.status)" class="hms-btn hms-btn-success hms-btn-sm" @click="act('arrive')"><i class="mdi mdi-check"></i> Mark arrived</button>
                    <button v-if="active.status==='arrived' && active.bed_offered_id" class="hms-btn hms-btn-success hms-btn-sm" @click="act('admit')"><i class="mdi mdi-bed"></i> Admit to bed</button>
                    <button v-if="['arrived','admitted'].includes(active.status)" class="hms-btn hms-btn-success hms-btn-sm" @click="completeMode=true"><i class="mdi mdi-flag-checkered"></i> Complete & counter-refer</button>
                  </template>
                  <button v-if="!['completed','cancelled','rejected'].includes(active.status)" class="hms-btn hms-btn-secondary hms-btn-sm" @click="act('cancel', { cancel_reason: prompt('Cancel reason?') })"><i class="mdi mdi-close"></i> Cancel</button>
                </div>
              </div>

              <!-- Complete + counter-referral form -->
              <div v-if="completeMode && active.status !== 'completed'" class="hms-card mt-2">
                <div class="hms-card-head">Complete & send counter-referral</div>
                <div class="hms-card-body">
                  <div class="row g-2">
                    <div class="col-12"><label class="hms-label">Outcome summary</label><textarea v-model="completeForm.outcome_summary" class="hms-input" rows="2"></textarea></div>
                    <div class="col-12"><label class="hms-label">Discharge diagnosis</label><input v-model="completeForm.discharge_diagnosis" class="hms-input" /></div>
                    <div class="col-12"><label class="hms-label">Discharge medications</label><textarea v-model="completeForm.discharge_medications" class="hms-input" rows="2"></textarea></div>
                    <div class="col-12"><label class="hms-label">Follow-up plan</label><textarea v-model="completeForm.follow_up_plan" class="hms-input" rows="2"></textarea></div>
                    <div class="col-12"><label class="hms-label">Feedback to referring clinician</label><textarea v-model="completeForm.feedback_to_referrer" class="hms-input" rows="3"></textarea></div>
                  </div>
                  <div class="text-end mt-2">
                    <button class="hms-btn hms-btn-secondary me-1" @click="completeMode=false">Cancel</button>
                    <button class="hms-btn hms-btn-success" :disabled="saving" @click="finalizeComplete">{{ saving?'Saving…':'Finalise' }}</button>
                  </div>
                </div>
              </div>

              <!-- Timeline -->
              <div class="hms-card mt-2">
                <div class="hms-card-head">Timeline</div>
                <div class="hms-card-body">
                  <div v-for="e in events" :key="e.event_id" class="hms-timeline-row">
                    <div class="hms-timeline-dot"></div>
                    <div>
                      <strong>{{ e.event_type }}</strong>
                      <span class="hms-muted-sm ms-2">{{ formatDt(e.event_at) }}</span>
                      <div v-if="e.event_notes" class="hms-muted-sm">{{ e.event_notes }}</div>
                    </div>
                  </div>
                  <div v-if="!events.length" class="hms-muted text-center" style="padding:12px">No events yet.</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ============ BED-FINDER MODAL ============ -->
    <div v-if="bedModal" class="hms-modal-bg" @click.self="bedModal=false">
      <div class="hms-modal" style="width:760px">
        <div class="hms-modal-head"><h5>Bed-finder — accept inbound referral</h5><button class="hms-icon-btn" @click="bedModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="hms-modal-body">
          <div class="hms-bed-summary mb-3">
            <div v-for="w in bedSummary" :key="w.ward_id" class="hms-bed-ward" :class="{ active: filterWard === w.ward_type }" @click="filterWard = (filterWard===w.ward_type ? '' : w.ward_type); reloadBeds()">
              <div class="hms-bed-ward-name">{{ w.ward_name }}</div>
              <div class="hms-bed-ward-num">
                <span class="st-good">{{ w.available }}</span>
                <span class="hms-muted-sm"> / {{ w.total }}</span>
              </div>
              <div class="hms-muted-sm">{{ w.ward_type }}</div>
            </div>
          </div>

          <div v-if="!availableBeds.length" class="text-center hms-pad hms-muted">No available beds matching filter.</div>
          <div v-else class="hms-bed-grid">
            <div v-for="b in availableBeds" :key="b.bed_id" class="hms-bed" :class="{ selected: selectedBed === b.bed_id }" @click="selectedBed = b.bed_id">
              <div class="hms-bed-num"><i class="mdi mdi-bed"></i> {{ b.bed_number }}</div>
              <div class="hms-muted-sm">{{ b.ward_name }} · {{ b.bed_type }}</div>
              <div class="hms-muted-sm">
                <span v-if="b.has_oxygen" class="hms-pill hms-pill-info">O₂</span>
                <span v-if="b.has_ventilator" class="hms-pill hms-pill-warn">vent</span>
                <span v-if="b.has_monitor" class="hms-pill hms-pill-info">mon</span>
              </div>
            </div>
          </div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="bedModal=false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="!selectedBed || saving" @click="acceptWithBed">{{ saving?'Accepting…':'Accept & reserve bed' }}</button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import Navigation from "@/components/hms-auth/Navigation.vue";
import { executeJson, hms } from "@/api";

export default {
  name: "ReferralsManagement",
  components: { Navigation },
  data() {
    return {
      direction: 'out', saving: false,
      referrals: [], stats: {},
      createModal: false, form: this.blankForm('outbound'),
      detailModal: false, active: {}, events: [], attachments: [],
      completeMode: false, completeForm: this.blankComplete(),
      bedModal: false, availableBeds: [], bedSummary: [], selectedBed: null, filterWard: ''
    };
  },
  computed: {
    hasVitals() {
      const a = this.active;
      return a.vital_temp || a.vital_pulse || a.vital_resp || a.vital_bp || a.vital_spo2 || a.vital_gcs;
    }
  },
  mounted() { this.load(); },
  methods: {
    blankForm(type) {
      return {
        referral_type: type, patient_id: null,
        urgency: 'routine', transport_mode: 'none', transport_needed: 0,
        receiving_facility_name: '', receiving_facility_code: '', receiving_contact: '',
        receiving_department: '', receiving_specialty: '',
        referring_facility_name: '', referring_facility_code: '', referring_contact: '',
        referral_reason: '', presenting_complaint: '', history: '', examination_findings: '',
        provisional_diagnosis: '', icd11_codes: '',
        investigations_done: '', investigations_results: '',
        current_medications: '', treatment_given: '', known_allergies: '',
        vital_temp: null, vital_pulse: null, vital_resp: null, vital_bp: '', vital_spo2: null, vital_gcs: null,
        accompanying_staff: '', expected_arrival: '',
        consent_obtained: 0, consent_signed_by: ''
      };
    },
    blankComplete() {
      return { outcome_summary: '', discharge_diagnosis: '', discharge_medications: '', follow_up_plan: '', feedback_to_referrer: '' };
    },

    async load() {
      try {
        const r = await executeJson({ function: 'getReferrals', direction: this.direction }, hms);
        if (r.data && r.data.success) {
          const d = r.data.data || {};
          this.referrals = d.Referrals || [];
          this.stats = d.Stats || {};
        }
      } catch (e) { console.error(e); }
    },

    openCreate(type) { this.form = this.blankForm(type); this.createModal = true; },

    async _create(sendNow) {
      this.saving = true;
      try {
        const r = await executeJson({ function: 'createReferral', ...this.form, send_now: sendNow ? 1 : 0 }, hms);
        if (r.data && r.data.success) {
          this.createModal = false;
          await this.load();
          alert(r.data.message || 'Saved.');
        } else alert((r.data && r.data.message) || 'Failed.');
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },
    saveDraft() { this._create(false); },
    saveAndSend() { this._create(true); },

    async openDetail(r) {
      this.completeMode = false;
      const res = await executeJson({ function: 'getReferralDetail', referral_id: r.referral_id }, hms);
      if (res.data && res.data.success) {
        const d = res.data.data || {};
        this.active = d.Referral || {};
        this.events = d.Events || [];
        this.attachments = d.Attachments || [];
        this.completeForm = this.blankComplete();
        this.detailModal = true;
      }
    },
    async refreshDetail() {
      const res = await executeJson({ function: 'getReferralDetail', referral_id: this.active.referral_id }, hms);
      if (res.data && res.data.success) {
        this.active = res.data.data.Referral || this.active;
        this.events = res.data.data.Events || [];
      }
    },
    async act(action, extra) {
      if (extra && extra.rejection_reason === null) return;
      if (extra && extra.cancel_reason === null) return;
      const r = await executeJson({ function: 'updateReferralStatus', referral_id: this.active.referral_id, action, ...(extra || {}) }, hms);
      if (r.data && r.data.success) { await this.refreshDetail(); await this.load(); }
      else alert((r.data && r.data.message) || 'Failed.');
    },
    async finalizeComplete() {
      this.saving = true;
      try {
        const r = await executeJson({ function: 'updateReferralStatus', referral_id: this.active.referral_id, action: 'complete', ...this.completeForm }, hms);
        if (r.data && r.data.success) { this.completeMode = false; await this.refreshDetail(); await this.load(); }
        else alert((r.data && r.data.message) || 'Failed.');
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },

    async openBedFinder() {
      this.selectedBed = null; this.filterWard = '';
      await this.reloadBeds();
      this.bedModal = true;
    },
    async reloadBeds() {
      const r = await executeJson({ function: 'getAvailableBeds', ward_type: this.filterWard }, hms);
      if (r.data && r.data.success) {
        this.availableBeds = r.data.data.Beds || [];
        this.bedSummary = r.data.data.WardSummary || [];
      }
    },
    async acceptWithBed() {
      if (!this.selectedBed) return;
      this.saving = true;
      try {
        const r = await executeJson({ function: 'updateReferralStatus', referral_id: this.active.referral_id, action: 'accept', bed_offered_id: this.selectedBed }, hms);
        if (r.data && r.data.success) { this.bedModal = false; await this.refreshDetail(); await this.load(); }
        else alert((r.data && r.data.message) || 'Failed.');
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },

    // formatting
    rowClass(r) {
      if (r.urgency === 'emergency' && !['completed','cancelled'].includes(r.status)) return 'row-action';
      if (r.status === 'rejected') return 'row-muted';
      if (r.status === 'in_transit') return 'row-warn';
      if (r.status === 'accepted') return 'row-good';
      return '';
    },
    statusPill(s) { return ({
      draft:'hms-pill-muted', sent:'hms-pill-warn', received:'hms-pill-info',
      accepted:'hms-pill-success', rejected:'hms-pill-danger',
      in_transit:'hms-pill-warn', arrived:'hms-pill-success', admitted:'hms-pill-success',
      completed:'hms-pill-muted', cancelled:'hms-pill-muted'
    })[s] || ''; },
    urgencyPill(u) { return ({ routine:'hms-pill-info', urgent:'hms-pill-warn', emergency:'hms-pill-danger' })[u] || ''; },
    formatStatus(s) { return (s || '').replace(/_/g, ' '); },
    formatTransport(t) { return ({ none:'None', self:'Self', ambulance_bls:'Ambulance (BLS)', ambulance_als:'Ambulance (ALS)', air:'Air', police:'Police' })[t] || t; },
    ageYears(dob) { if (!dob) return '?'; const d = new Date(dob); if (isNaN(d)) return '?'; return Math.floor((Date.now() - d.getTime()) / 31557600000); },
    formatDt(d) { if (!d) return ''; return new Date(d.replace(' ','T')).toLocaleString(); }
  }
};
</script>

<style scoped>
.hms-page-body { padding: 16px 0 32px; }
.hms-back { color: #4a5568; font-size: 20px; text-decoration: none; }
.hms-page-title { font-size: 18px; font-weight: 600; color: #2d3748; }

.hms-tab-row { display: flex; gap: 4px; border-bottom: 1px solid #e2e8f0; margin-bottom: 14px; }
.hms-tab { padding: 10px 16px; border: none; background: none; cursor: pointer; color: #718096; border-bottom: 2px solid transparent; font-size: 14px; }
.hms-tab.active { color: #2b6cb0; border-bottom-color: #2b6cb0; font-weight: 600; }

.hms-stat-row { display: grid; grid-template-columns: repeat(7, 1fr); gap: 10px; margin-bottom: 14px; }
.hms-stat { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 12px; text-align: center; }
.hms-stat-num { font-size: 24px; font-weight: 700; color: #1a202c; }
.hms-stat-lbl { font-size: 11px; color: #718096; text-transform: uppercase; letter-spacing: .4px; margin-top: 2px; }
.hms-stat-info { color: #2c5282; } .hms-stat-success { color: #276749; } .hms-stat-warning { color: #c05621; } .hms-stat-danger { color: #c53030; }

.hms-table { width: 100%; border-collapse: collapse; font-size: 13px; background: #fff; }
.hms-table th { background: #f7fafc; padding: 9px; border-bottom: 1px solid #e2e8f0; font-weight: 600; color: #4a5568; text-align: left; }
.hms-table td { padding: 8px 9px; border-bottom: 1px solid #edf2f7; vertical-align: middle; }
.hms-table tr.row-warn   { background: #fffaf0; }
.hms-table tr.row-action { background: #fff5f5; }
.hms-table tr.row-good   { background: #f0fff4; }
.hms-table tr.row-muted  { color: #a0aec0; }

.hms-input { width: 100%; padding: 6px 10px; border: 1px solid #cbd5e0; border-radius: 6px; font-size: 13px; }
.hms-label { font-size: 11px; font-weight: 600; color: #4a5568; display: block; margin-bottom: 3px; }
.hms-section-lbl { font-size: 11px; font-weight: 700; color: #718096; text-transform: uppercase; letter-spacing: .5px; margin-top: 6px; padding-bottom: 3px; border-bottom: 1px solid #e2e8f0; }

.hms-pill { display: inline-flex; align-items: center; gap: 3px; padding: 2px 8px; border-radius: 10px; font-size: 11px; font-weight: 600; }
.hms-pill-success { background: #c6f6d5; color: #22543d; }
.hms-pill-info    { background: #bee3f8; color: #2a4365; }
.hms-pill-warn    { background: #feebc8; color: #7b341e; }
.hms-pill-danger  { background: #fed7d7; color: #742a2a; }
.hms-pill-muted   { background: #edf2f7; color: #4a5568; }

.hms-modal-bg { position: fixed; inset: 0; background: rgba(0,0,0,.55); display: flex; align-items: center; justify-content: center; z-index: 100; padding: 20px; }
.hms-modal { background: #fff; border-radius: 8px; max-height: 95vh; display: flex; flex-direction: column; overflow: hidden; }
.hms-modal-head { padding: 14px 18px; border-bottom: 1px solid #e2e8f0; display: flex; justify-content: space-between; align-items: center; }
.hms-modal-body { padding: 16px 18px; overflow-y: auto; }
.hms-modal-foot { padding: 12px 18px; border-top: 1px solid #e2e8f0; display: flex; justify-content: flex-end; gap: 8px; }
.hms-icon-btn { background: none; border: none; cursor: pointer; }

.hms-detail-modal { width: 1100px; max-width: 96vw; }

.hms-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 6px; }
.hms-card-head { padding: 8px 12px; border-bottom: 1px solid #e2e8f0; font-weight: 600; color: #2d3748; font-size: 13px; }
.hms-card-body { padding: 12px; font-size: 13px; }
.hms-card-body > div { margin-bottom: 4px; }

.hms-feedback { background: #ebf8ff; padding: 10px; border-radius: 6px; border-left: 3px solid #2b6cb0; }

.hms-timeline-row { display: flex; gap: 10px; padding: 6px 0; border-bottom: 1px dashed #edf2f7; }
.hms-timeline-row:last-child { border-bottom: 0; }
.hms-timeline-dot { width: 10px; height: 10px; background: #2b6cb0; border-radius: 50%; margin-top: 4px; flex-shrink: 0; }

.hms-bed-summary { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; }
.hms-bed-ward { background: #fff; border: 1px solid #e2e8f0; border-radius: 6px; padding: 10px; cursor: pointer; text-align: center; }
.hms-bed-ward.active { background: #ebf8ff; border-color: #2b6cb0; }
.hms-bed-ward-name { font-weight: 600; font-size: 13px; }
.hms-bed-ward-num { font-size: 22px; font-weight: 700; }

.hms-bed-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; }
.hms-bed { background: #f0fff4; border: 1px solid #c6f6d5; border-radius: 6px; padding: 10px; cursor: pointer; }
.hms-bed.selected { background: #fefcbf; border-color: #d69e2e; box-shadow: 0 0 0 2px #d69e2e; }
.hms-bed-num { font-weight: 700; font-size: 14px; }

.hms-btn { display: inline-flex; align-items: center; gap: 4px; padding: 7px 12px; border-radius: 6px; border: 1px solid transparent; font-size: 13px; font-weight: 500; cursor: pointer; }
.hms-btn-sm { padding: 4px 10px; font-size: 12px; }
.hms-btn-blue      { background: #2b6cb0; color: #fff; }
.hms-btn-success   { background: #38a169; color: #fff; }
.hms-btn-warning   { background: #d69e2e; color: #fff; }
.hms-btn-secondary { background: #fff; color: #2d3748; border: 1px solid #cbd5e0; }

.st-bad { color: #c53030; font-weight: 600; }
.st-warn { color: #b45309; font-weight: 600; }
.st-good { color: #276749; font-weight: 600; }
.hms-muted { color: #a0aec0; }
.hms-muted-sm { color: #a0aec0; font-size: 11px; }
.hms-pad { padding: 24px; }
</style>
